import { userService } from './userService';

// Simular delay de red
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class StudentService {
  // Obtener todos los alumnos
  async getStudents(filters = {}) {
    await delay(300);

    try {
      // Obtener todos los usuarios con rol de alumno
      const response = await userService.getUsers({
        ...filters,
        role: 'alumno'
      });

      if (!response.success) {
        return response;
      }

      // Obtener apoderados para relacionar
      const guardiansResponse = await userService.getUsers({
        role: 'apoderado'
      });

      const guardians = guardiansResponse.success ? guardiansResponse.data : [];

      // Enriquecer datos de alumnos con información de apoderados
      const enrichedStudents = response.data.map(student => {
        // Buscar apoderados que tengan a este alumno vinculado
        const studentGuardians = guardians.filter(guardian =>
          guardian.children && guardian.children.some(child => child.id === student.id)
        );

        return {
          ...student,
          guardians: studentGuardians.map(g => ({
            id: g.id,
            name: g.name,
            email: g.email,
            phone: g.phone
          }))
        };
      });

      return {
        success: true,
        data: enrichedStudents,
        total: enrichedStudents.length
      };
    } catch (error) {
      console.error('Error al obtener alumnos:', error);
      return {
        success: false,
        message: 'Error al obtener alumnos'
      };
    }
  }

  // Obtener un alumno por ID
  async getStudentById(id) {
    await delay(300);

    try {
      const response = await userService.getUserById(id);

      if (!response.success) {
        return response;
      }

      const student = response.data;

      // Verificar que sea un alumno
      if (student.role !== 'alumno') {
        return {
          success: false,
          message: 'El usuario no es un alumno'
        };
      }

      // Obtener apoderados
      const guardiansResponse = await userService.getUsers({
        role: 'apoderado'
      });

      const guardians = guardiansResponse.success ? guardiansResponse.data : [];

      // Buscar apoderados de este alumno
      const studentGuardians = guardians.filter(guardian =>
        guardian.children && guardian.children.some(child => child.id === student.id)
      );

      // Generar datos mock de anotaciones
      const annotations = this.generateMockAnnotations(student);

      // Generar datos mock de historial de cursos
      const courseHistory = this.generateMockCourseHistory(student);

      // Generar datos mock de proyectos
      const projects = this.generateMockProjects(student);

      return {
        success: true,
        data: {
          ...student,
          guardians: studentGuardians.map(g => ({
            id: g.id,
            name: g.name,
            email: g.email,
            phone: g.phone,
            address: g.address
          })),
          annotations,
          courseHistory,
          projects
        }
      };
    } catch (error) {
      console.error('Error al obtener alumno:', error);
      return {
        success: false,
        message: 'Error al obtener alumno'
      };
    }
  }

  // Obtener estadísticas de alumnos
  async getStudentStats() {
    await delay(200);

    try {
      const response = await this.getStudents();

      if (!response.success) {
        return {
          success: false,
          message: 'Error al obtener estadísticas'
        };
      }

      const students = response.data;

      // Agrupar por curso
      const byCourse = students.reduce((acc, student) => {
        const course = student.course || 'Sin curso';
        acc[course] = (acc[course] || 0) + 1;
        return acc;
      }, {});

      // Contar activos/inactivos
      const activeCount = students.filter(s => s.status === 'activo').length;
      const inactiveCount = students.filter(s => s.status === 'inactivo').length;

      // Contar alumnos con/sin apoderados
      const withGuardians = students.filter(s => s.guardians && s.guardians.length > 0).length;
      const withoutGuardians = students.length - withGuardians;

      return {
        success: true,
        data: {
          total: students.length,
          active: activeCount,
          inactive: inactiveCount,
          withGuardians,
          withoutGuardians,
          byCourse
        }
      };
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      return {
        success: false,
        message: 'Error al obtener estadísticas'
      };
    }
  }

  // Generar anotaciones mock
  generateMockAnnotations(student) {
    const annotationTypes = ['positiva', 'negativa', 'neutral'];
    const courses = ['7° Básico A', '8° Básico A', '1° Medio A', '2° Medio A'];

    const positiveAnnotations = [
      { title: 'Excelente participación', description: 'Participó activamente en clase respondiendo preguntas y colaborando con sus compañeros' },
      { title: 'Trabajo destacado', description: 'Entregó un trabajo de calidad superior al promedio del curso' },
      { title: 'Colaboración ejemplar', description: 'Ayudó a sus compañeros en actividades grupales demostrando liderazgo' },
      { title: 'Mejora significativa', description: 'Mostró progreso notable en su rendimiento académico' }
    ];

    const negativeAnnotations = [
      { title: 'Atraso reiterado', description: 'Llegó tarde a clases sin justificación en múltiples ocasiones' },
      { title: 'Conducta inapropiada', description: 'Interrumpió la clase con comportamiento disruptivo' },
      { title: 'Falta de material', description: 'No trajo materiales necesarios para la clase' },
      { title: 'Incumplimiento de tareas', description: 'No entregó tareas asignadas en el plazo establecido' }
    ];

    const neutralAnnotations = [
      { title: 'Justificación de inasistencia', description: 'Presentó certificado médico por ausencia de 3 días' },
      { title: 'Cambio de grupo', description: 'Se realizó cambio de grupo de trabajo según solicitud del apoderado' },
      { title: 'Reunión con apoderado', description: 'Se sostuvo reunión con apoderado para seguimiento académico' }
    ];

    const numAnnotations = Math.floor(Math.random() * 8) + 2; // 2-10 anotaciones
    const annotations = [];

    for (let i = 0; i < numAnnotations; i++) {
      const type = annotationTypes[Math.floor(Math.random() * annotationTypes.length)];
      let annotation;

      if (type === 'positiva') {
        annotation = positiveAnnotations[Math.floor(Math.random() * positiveAnnotations.length)];
      } else if (type === 'negativa') {
        annotation = negativeAnnotations[Math.floor(Math.random() * negativeAnnotations.length)];
      } else {
        annotation = neutralAnnotations[Math.floor(Math.random() * neutralAnnotations.length)];
      }

      // Generar fecha aleatoria en los últimos 365 días
      const daysAgo = Math.floor(Math.random() * 365);
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);

      annotations.push({
        date: date.toISOString().split('T')[0],
        course: student.course || courses[Math.floor(Math.random() * courses.length)],
        type,
        title: annotation.title,
        description: annotation.description
      });
    }

    // Ordenar por fecha descendente
    return annotations.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // Generar historial de cursos mock
  generateMockCourseHistory(student) {
    const currentYear = new Date().getFullYear();
    const history = [];

    // Generar historial de 3-5 años
    const yearsCount = Math.floor(Math.random() * 3) + 3;

    for (let i = 0; i < yearsCount; i++) {
      const year = currentYear - i;
      const grade = 8 - i; // Asumir que está en 8° ahora

      if (grade < 1) break; // No mostrar antes de 1° básico

      let course;
      if (grade <= 8) {
        course = `${grade}° Básico`;
      } else {
        const medioGrade = grade - 8;
        course = `${medioGrade}° Medio`;
      }

      // Generar promedio aleatorio entre 4.0 y 7.0
      const average = (Math.random() * 3 + 4).toFixed(1);
      const status = parseFloat(average) >= 4.0 ? 'aprobado' : 'reprobado';

      history.push({
        year,
        course: i === 0 ? (student.course || course) : course,
        average,
        status: i === 0 ? 'en_curso' : status
      });
    }

    return history;
  }

  // Generar proyectos mock
  generateMockProjects(student) {
    const projectTemplates = [
      {
        name: 'Energías Renovables en Chile',
        description: 'Investigación sobre el potencial de energías renovables en diferentes regiones del país'
      },
      {
        name: 'Biodiversidad Local',
        description: 'Estudio de la flora y fauna nativa de la región metropolitana'
      },
      {
        name: 'Historia de Santiago',
        description: 'Proyecto sobre la evolución histórica y cultural de Santiago de Chile'
      },
      {
        name: 'Matemáticas en la Vida Cotidiana',
        description: 'Aplicación de conceptos matemáticos en situaciones de la vida diaria'
      },
      {
        name: 'Contaminación Ambiental',
        description: 'Análisis del impacto de la contaminación en la salud y el medio ambiente'
      },
      {
        name: 'Literatura Chilena Contemporánea',
        description: 'Estudio de autores y obras representativas de la literatura nacional'
      }
    ];

    const numProjects = Math.floor(Math.random() * 4) + 2; // 2-5 proyectos
    const projects = [];
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < numProjects; i++) {
      const template = projectTemplates[Math.floor(Math.random() * projectTemplates.length)];
      const year = currentYear - Math.floor(Math.random() * 2); // Últimos 2 años
      const semester = Math.random() > 0.5 ? 1 : 2;
      const status = i === 0 ? 'en_progreso' : (Math.random() > 0.2 ? 'completado' : 'pendiente');

      let grade = null;
      if (status === 'completado') {
        grade = (Math.random() * 3 + 4).toFixed(1); // 4.0 - 7.0
      }

      projects.push({
        name: template.name,
        description: template.description,
        period: `${semester}° Semestre ${year}`,
        course: student.course || '8° Básico A',
        status,
        grade
      });
    }

    return projects;
  }
}

export const studentService = new StudentService();
